import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { AnimatePresence, motion } from 'framer-motion';
import { customerApi } from '../../api/customerApi';
import { formationApi } from '../../api/formationApi';
import { questionnaireApi } from '../../api/questionnaireApi';
import { sessionApi } from '../../api/sessionApi';
import usePermissions from '../../hooks/usePermissions';
import { getLearnerStatusTone, getTone, grey, lightVeil, toneAlpha } from '../../theme/tones';

/**
 * Actions rapides — le bouton rond en bas à droite de toutes les pages du CRM.
 *
 * Chaque action rejoint le parcours qui existe déjà, elle ne le réécrit pas :
 * les deux créations simples ouvrent leur page de création, les trois autres
 * appellent exactement les mêmes API que l'écran d'où on les fait d'habitude
 * (la page de création de session, `addCustomerFormation` depuis la fiche
 * apprenant, `editCustomer` depuis le tableau des apprenants d'une session).
 * Aucune règle métier n'est dupliquée ici : ce fichier raccourcit le chemin.
 *
 * Volontairement absent : le passage au statut « Décalé ». Il ouvre, dans le
 * tableau de la session, un parcours à part entière (choix de la session
 * d'accueil, report de l'inscription). Le proposer ici sans ce parcours
 * produirait des décalages incomplets.
 */

/** Statuts proposés, dans l'ordre du tableau de la session. */
const STATUSES = [
  'Inscrit',
  'En attente',
  'À décaler',
  '- de 5 personnes',
  'Annulation',
  'À transférer',
  'Désinscrit'
];

/** Statuts pour lesquels le serveur enregistre aussi la date de désinscription. */
const CLOSING_STATUSES = ['À décaler', 'Annulation', 'Désinscrit'];

const ACTIONS = [
  {
    key: 'customer',
    label: 'Ajouter un apprenant',
    hint: 'Nouvelle fiche apprenant',
    perm: 'customers.view',
    tone: 'blue',
    icon: PersonAddAlt1Icon,
    to: '/dashboard/customers/create'
  },
  {
    key: 'session',
    label: 'Ajouter une session',
    hint: "Choisir la formation d'abord",
    perm: 'sessions.create',
    tone: 'violet',
    icon: EventAvailableIcon
  },
  {
    key: 'formation',
    label: 'Ajouter une formation',
    hint: 'Nouvelle fiche formation',
    perm: 'formations.create',
    tone: 'indigo',
    icon: MenuBookIcon,
    to: '/dashboard/formations/create'
  },
  {
    key: 'enroll',
    label: 'Inscrire un apprenant',
    hint: 'À une session existante',
    perm: 'sessions.learners.add',
    tone: 'emerald',
    icon: HowToRegIcon
  },
  {
    key: 'status',
    label: 'Changer le statut',
    hint: "D'une inscription",
    perm: 'sessions.learners.edit',
    tone: 'amber',
    icon: SwapHorizIcon
  }
];

/**
 * Géométrie du bouton, exportée parce qu'elle ne concerne pas que lui.
 *
 * Le bouton flotte au-dessus du contenu : sans marge en bas des pages, il
 * recouvre ce qui s'y trouve — et les tableaux y mettent justement leur
 * pagination. DashboardLayout réserve la place à partir de ces valeurs, de
 * sorte qu'en changer une ici suffit.
 */
export const QUICK_ACTIONS_SIZE = 58;
export const QUICK_ACTIONS_BOTTOM = { xs: 16, md: 28 };
/** Marge à réserver sous le contenu : le bouton, son écart au bord, et un jour. */
export const QUICK_ACTIONS_GUTTER = {
  xs: QUICK_ACTIONS_BOTTOM.xs + QUICK_ACTIONS_SIZE + 12,
  md: QUICK_ACTIONS_BOTTOM.md + QUICK_ACTIONS_SIZE + 12
};

const nomComplet = (c) => (c ? `${c.firstname || ''} ${c.lastname || ''}`.trim() : '');

/**
 * Le catalogue réellement proposable : formation active ET publiée (DPC ou
 * FIF-PL). Sur apteed cela fait 56 formations, là où le seul `etat = 'Active'`
 * en donnerait 197 — les 136 en « Désactivation » ne se vendent plus, et on
 * n'y crée ni session ni inscription.
 *
 * Règle positive volontairement : écarter « Désactivation » et « Supprimée »
 * en laisserait 61, dont cinq qui ne sont publiées nulle part.
 */
const estPubliee = (f) => (f.etat || '') === 'Active' && String(f.status || '').startsWith('Publication');

/**
 * Sélecteur d'apprenant, par recherche.
 *
 * Réutilise la recherche de la fiche apprenant (`getRelationCandidates`) :
 * même requête, mêmes 50 premiers résultats. Deux caractères minimum, sinon
 * la liste serait les 50 premiers noms de la base, ce qui n'aide personne.
 */
const LearnerPicker = ({ value, onChange, label }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    if (query.trim().length < 2) {
      setOptions([]);
      return undefined;
    }
    const current = ++requestId.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await customerApi.getRelationCandidates(
          query.trim(),
          '',
          window.localStorage.getItem('accessToken')
        );
        // Une frappe plus récente a déjà été lancée : sa réponse fait foi.
        if (current !== requestId.current) return;
        setOptions(res || []);
      } catch (err) {
        console.error(err);
      } finally {
        if (current === requestId.current) setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  return (
    <Autocomplete
      options={options}
      value={value}
      filterOptions={(x) => x}
      onInputChange={(e, v) => setQuery(v)}
      onChange={(e, v) => onChange(v)}
      getOptionLabel={(o) => (o ? `${nomComplet(o)}${o.profession ? ` — ${o.profession}` : ''}` : '')}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      loading={loading}
      noOptionsText={query.trim().length < 2 ? 'Tapez au moins 2 caractères' : 'Aucun apprenant'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
        />
      )}
    />
  );
};

const QuickActions = () => {
  const { can } = usePermissions();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  // Catalogue chargé à la première ouverture d'un dialogue qui en a besoin,
  // puis gardé : inutile de le redemander à chaque action rapide.
  const [formations, setFormations] = useState([]);
  const [formation, setFormation] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState(null);
  const [learner, setLearner] = useState(null);
  const [inscriptions, setInscriptions] = useState([]);
  const [inscription, setInscription] = useState(null);
  const [status, setStatus] = useState('');

  // Tant que l'API ne renvoie pas de permissions (back pas encore à jour),
  // on laisse passer : c'est déjà le défaut de PermissionGuard, et les écrans
  // visés restent accessibles dans ce mode. Une fois le back déployé, les
  // permissions font foi.
  const actions = useMemo(() => ACTIONS.filter((a) => can(a.perm, true)), [can]);

  const closeDialog = () => {
    setDialog(null);
    setFormation(null);
    setSessions([]);
    setSession(null);
    setLearner(null);
    setInscriptions([]);
    setInscription(null);
    setStatus('');
  };

  const loadFormations = useCallback(async () => {
    if (formations.length > 0) return;
    try {
      const data = await formationApi.getFormationOptions(window.localStorage.getItem('accessToken'));
      setFormations((data || []).filter(estPubliee));
    } catch (err) {
      console.error(err);
      toast.error('Impossible de charger les formations.');
    }
  }, [formations.length]);

  // Les sessions suivent la formation choisie, comme dans la fiche apprenant.
  useEffect(() => {
    if (!formation) {
      setSessions([]);
      setSession(null);
      return;
    }
    (async () => {
      try {
        const data = await questionnaireApi.getSessionsByFormationId(
          formation.id,
          window.localStorage.getItem('accessToken')
        );
        setSessions(data || []);
      } catch (err) {
        console.error(err);
        setSessions([]);
      }
    })();
  }, [formation]);

  // Inscriptions de l'apprenant choisi : la fiche apprenant les sert déjà,
  // avec l'identifiant de ligne (`learner_session.id`) attendu par editCustomer.
  useEffect(() => {
    if (!learner) {
      setInscriptions([]);
      setInscription(null);
      return;
    }
    (async () => {
      try {
        const data = await customerApi.getCustomer(
          learner.id,
          window.localStorage.getItem('accessToken')
        );
        setInscriptions((data && data.formations) || []);
      } catch (err) {
        console.error(err);
        setInscriptions([]);
      }
    })();
  }, [learner]);

  const handleAction = (action) => {
    setOpen(false);
    if (action.to) {
      navigate(action.to);
      return;
    }
    if (action.key === 'session' || action.key === 'enroll') loadFormations();
    setDialog(action.key);
  };

  // Création de session : la page existante attend la formation dans l'URL.
  const goToSessionCreate = () => {
    if (!formation) return;
    const id = formation.id;
    closeDialog();
    navigate(`/dashboard/session/create/${id}`);
  };

  const submitEnroll = async () => {
    if (!learner || !session) return;
    setBusy(true);
    try {
      const res = await sessionApi.addCustomerFormation(
        learner.id,
        session.id,
        window.localStorage.getItem('accessToken')
      );
      // Le serveur refuse une inscription en double (400). L'annoncer plutôt
      // que de féliciter l'utilisateur pour une action qui n'a rien fait.
      if (!res || res.status !== 200) {
        toast.error(res && res.message ? res.message : "L'inscription n'a pas pu être enregistrée.");
        return;
      }
      toast.success(`${nomComplet(learner)} a été inscrit.`);
      closeDialog();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'inscription.");
    } finally {
      setBusy(false);
    }
  };

  const submitStatus = async () => {
    if (!inscription || !status) return;
    setBusy(true);
    try {
      // Même appel que le tableau des apprenants d'une session : type 2,
      // champ `status`, identifiant de la ligne d'inscription.
      const res = await sessionApi.editCustomer(
        2,
        inscription.id,
        status,
        window.localStorage.getItem('accessToken'),
        'status'
      );
      if (!res || res.status !== 200) {
        toast.error(res && res.message ? res.message : "Le statut n'a pas pu être modifié.");
        return;
      }
      toast.success(`Statut passé à « ${status} ».`);
      closeDialog();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du changement de statut.');
    } finally {
      setBusy(false);
    }
  };

  const libelleSession = (s) => {
    if (!s) return '';
    const date = s.start_date ? new Date(s.start_date).toLocaleDateString('fr-FR') : 'date à définir';
    return `N° ${s.number} • ${date}${s.status ? ` • ${s.status}` : ''}`;
  };

  const libelleInscription = (i) => {
    if (!i) return '';
    const date = i.start_date ? new Date(i.start_date).toLocaleDateString('fr-FR') : 'date à définir';
    return `${i.name} — session ${i.number_session} • ${date} • ${i.status}`;
  };

  if (actions.length === 0) return null;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: QUICK_ACTIONS_BOTTOM,
          right: { xs: 16, md: 28 },
          zIndex: 1200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1.25
        }}
      >
        <AnimatePresence>
          {open && actions.map((action, index) => {
            const tone = getTone(action.tone);
            const Icone = action.icon;
            return (
              <motion.div
                key={action.key}
                initial={{ opacity: 0, y: 12, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 12, scale: 0.9 }}
                // Les entrées se posent de bas en haut, la plus proche du
                // bouton d'abord : le regard suit le geste.
                transition={{ duration: 0.18, delay: (actions.length - 1 - index) * 0.03 }}
              >
                <Box
                  onClick={() => handleAction(action)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    pl: 2,
                    pr: 0.75,
                    py: 0.75,
                    cursor: 'pointer',
                    borderRadius: '999px',
                    bgcolor: lightVeil(0.92),
                    backdropFilter: 'blur(12px)',
                    border: `1px solid ${tone.border}`,
                    boxShadow: `0 10px 30px ${toneAlpha(action.tone, 0.18)}`,
                    transition: 'transform .18s ease, box-shadow .18s ease',
                    '&:hover': {
                      transform: 'translateX(-4px)',
                      boxShadow: `0 14px 36px ${toneAlpha(action.tone, 0.3)}`
                    }
                  }}
                >
                  <Box sx={{ textAlign: 'right', lineHeight: 1.15 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.86rem', color: 'text.primary' }}>
                      {action.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 600 }}>
                      {action.hint}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: tone.bgStrong,
                      color: tone.color,
                      flexShrink: 0
                    }}
                  >
                    <Icone sx={{ fontSize: 20 }} />
                  </Box>
                </Box>
              </motion.div>
            );
          })}
        </AnimatePresence>

        <Tooltip title={open ? '' : 'Actions rapides'} placement="left">
          <Fab
            color="primary"
            onClick={() => setOpen((v) => !v)}
            sx={{
              width: QUICK_ACTIONS_SIZE,
              height: QUICK_ACTIONS_SIZE,
              boxShadow: `0 12px 34px ${toneAlpha('brand', 0.45)}`,
              background: `linear-gradient(135deg, ${getTone('brand').base}, ${getTone('blue').base})`,
              '&:hover': { background: `linear-gradient(135deg, ${getTone('brand').deep}, ${getTone('blue').text})` }
            }}
          >
            <motion.div
              animate={{ rotate: open ? 135 : 0 }}
              transition={{ duration: 0.2 }}
              style={{ display: 'flex' }}
            >
              {open ? <CloseIcon /> : <AddIcon />}
            </motion.div>
          </Fab>
        </Tooltip>
      </Box>

      {/* Ajouter une session : choisir la formation, puis la page existante. */}
      <Dialog open={dialog === 'session'} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Ajouter une session</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Une session appartient à une formation. Choisissez-la, le formulaire de création s&apos;ouvre ensuite.
          </Typography>
          <Autocomplete
            options={formations}
            value={formation}
            onChange={(e, v) => setFormation(v)}
            getOptionLabel={(o) => (o ? `${o.name}${o.programNumberAll ? ` (N° ${o.programNumberAll})` : ''}` : '')}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField {...params} label="Formation" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }} />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={closeDialog} color="inherit" sx={{ fontWeight: 700 }}>Annuler</Button>
          <Button onClick={goToSessionCreate} variant="contained" disabled={!formation} sx={{ borderRadius: '12px', px: 3, fontWeight: 800 }}>
            Continuer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inscrire un apprenant : apprenant, formation, session. */}
      <Dialog open={dialog === 'enroll'} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Inscrire un apprenant</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <LearnerPicker value={learner} onChange={setLearner} label="Apprenant" />
            <Autocomplete
              options={formations}
              value={formation}
              onChange={(e, v) => setFormation(v)}
              getOptionLabel={(o) => (o ? `${o.name}${o.programNumberAll ? ` (N° ${o.programNumberAll})` : ''}` : '')}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField {...params} label="Formation" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }} />
              )}
            />
            <Autocomplete
              options={sessions}
              value={session}
              disabled={!formation}
              onChange={(e, v) => setSession(v)}
              getOptionLabel={libelleSession}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              noOptionsText="Aucune session pour cette formation"
              renderInput={(params) => (
                <TextField {...params} label="Session" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }} />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={closeDialog} color="inherit" sx={{ fontWeight: 700 }}>Annuler</Button>
          <Button onClick={submitEnroll} variant="contained" disabled={!learner || !session || busy} sx={{ borderRadius: '12px', px: 3, fontWeight: 800 }}>
            {busy ? 'Inscription…' : "Confirmer l'inscription"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Changer le statut d'une inscription. */}
      <Dialog open={dialog === 'status'} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Changer le statut d&apos;un apprenant</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <LearnerPicker value={learner} onChange={setLearner} label="Apprenant" />
            <Autocomplete
              options={inscriptions}
              value={inscription}
              disabled={!learner}
              onChange={(e, v) => { setInscription(v); setStatus(''); }}
              getOptionLabel={libelleInscription}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              noOptionsText="Aucune inscription pour cet apprenant"
              renderInput={(params) => (
                <TextField {...params} label="Inscription" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }} />
              )}
            />
            {inscription && (
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  borderRadius: '14px',
                  bgcolor: getTone(getLearnerStatusTone(inscription.status)).bg,
                  border: `1px solid ${getTone(getLearnerStatusTone(inscription.status)).border}`
                }}
              >
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary' }}>
                  Statut actuel : {inscription.status}
                </Typography>
              </Box>
            )}
            <TextField
              select
              SelectProps={{ native: true }}
              label="Nouveau statut"
              value={status}
              disabled={!inscription}
              onChange={(e) => setStatus(e.target.value)}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
            >
              <option value="">Choisir…</option>
              {STATUSES.filter((s) => !inscription || s !== inscription.status).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </TextField>
            {CLOSING_STATUSES.includes(status) && (
              <Typography sx={{ fontSize: '0.78rem', color: grey(600) }}>
                Ce statut enregistre aussi la date de désinscription, comme depuis la fiche de session.
              </Typography>
            )}
            <Typography sx={{ fontSize: '0.75rem', color: grey(500) }}>
              Le passage à « Décalé » reste sur la fiche de session : il demande de choisir la session d&apos;accueil.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={closeDialog} color="inherit" sx={{ fontWeight: 700 }}>Annuler</Button>
          <Button onClick={submitStatus} variant="contained" disabled={!inscription || !status || busy} sx={{ borderRadius: '12px', px: 3, fontWeight: 800 }}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuickActions;
